/*
  <mms-quick-look> — shared, view-only product popup.

  API
    window.MMSQuickLook.open(handle, { opener, action, variantId })
        Opens the popup for a product handle.
        opener  The element that triggered it; focus returns to it on close.
        variantId  Optional. Opens on that variant: the gallery starts on its image (the
                first image when it has none), and its option label and price show.
        action  Optional context action: { label, run(), activeLabel?, isActive?(), pendingLabel? }.
                When given, a primary button is shown in the info column. Its label is
                activeLabel while isActive() is true, else label (always label when
                isActive is absent). Clicking it calls run():
                  - returns nothing: the popup closes.
                  - returns a promise: the button is disabled and reads pendingLabel
                    ("Adding…") while pending; on resolve the popup closes, on reject it
                    stays open, the button re-enables and the rejection's message shows
                    under it.
                Without an action the popup shows no button.
    window.MMSQuickLook.close()          Closes the popup if open.
    window.MMSQuickLook.prefetch(handle) Starts loading (and caches) a product's content.

  Declarative triggers: any element with [data-quick-look-trigger] opens the popup (no
  action) for its own data-quick-look-handle, or the nearest ancestor's. Elements with
  [data-quick-look-trigger] or [data-quick-look-prefetch] prefetch on hover and focus.

  Content comes from /products/<handle>?view=quick-look (sections/quick-look.liquid),
  cached in memory per handle. Gallery (prev/next, dots, arrow keys, swipe via scroll-snap,
  live counter) and the description More/Less toggle are wired here after insertion.

  Drawer safety (Impulse cart drawer, #CartDrawer):
    - When the opener is inside #CartDrawer, the element is moved to be a direct child of
      #CartDrawer, outside [data-products] and the cart milestones mounts (all rebuilt with
      innerHTML). The drawer's window click handler ignores clicks whose target is inside
      .drawer, so clicks in the dialog don't close the drawer.
    - The drawer closes on window keyup Escape. The dialog closes on keydown (its cancel
      event), so on cancel a one-shot, capture-phase window keyup listener swallows the
      matching keyup.
    - Focus: on close it returns to the opener. From the drawer, if the opener is gone (or
      is removed by a redraw shortly after), it moves to the drawer's close button.
    - Scroll: from the page, html/body overflow is locked and restored exactly on close.
      From the drawer, nothing is touched; the drawer's own js-drawer-open lock stays as is.
    - modalOpen is never dispatched (it would close the drawer).
*/
(function () {
  'use strict';

  if (customElements.get('mms-quick-look')) {
    return;
  }

  var ESC_GUARD_MS = 1000;
  var cache = new Map();

  function rootUrl() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.charAt(root.length - 1) === '/' ? root : root + '/';
  }

  function productUrl(handle) {
    return rootUrl() + 'products/' + encodeURIComponent(handle);
  }

  // Resolves with the content element's HTML; a failed load is not cached.
  function load(handle) {
    if (cache.has(handle)) return cache.get(handle);
    var promise = fetch(productUrl(handle) + '?view=quick-look', {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' }
    })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.text();
      })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var content = doc.querySelector('[data-quick-look-content]');
        if (!content) throw new Error('No quick-look content');
        return content.outerHTML;
      });
    promise.catch(function () { cache.delete(handle); });
    cache.set(handle, promise);
    return promise;
  }

  var STYLES = [
    'mms-quick-look { display: contents; }',
    'dialog.quick-look {',
    '  padding: 0; border: 0; border-radius: 12px;',
    '  width: min(92vw, 760px); max-width: 760px; max-height: min(88vh, 640px);',
    '  background: var(--colorBody, #fff); color: var(--colorTextBody, #1a1a1a);',
    '  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.18); overflow: hidden;',
    '}',
    'dialog.quick-look[open] { display: flex; flex-direction: column; animation: mmsQlIn 0.2s ease; }',
    'dialog.quick-look::backdrop { background: rgba(0, 0, 0, 0.45); }',
    '@keyframes mmsQlIn { from { transform: translateY(12px); opacity: 0; } to { transform: none; opacity: 1; } }',
    '.quick-look__close {',
    '  position: absolute; top: 10px; right: 10px; z-index: 3;',
    '  width: 36px; height: 36px; border-radius: 50%; border: 0; cursor: pointer;',
    '  display: flex; align-items: center; justify-content: center;',
    '  background: var(--colorBody, #fff); color: var(--colorTextBody, #333);',
    '  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15); font-size: 22px; line-height: 1;',
    '}',
    '.quick-look__close:focus-visible, .quick-look__nav:focus-visible, .quick-look__dot:focus-visible,',
    '.quick-look__more:focus-visible, .quick-look__link:focus-visible, .quick-look__action:focus-visible,',
    '.quick-look__track:focus-visible { outline: 2px solid var(--colorBtnPrimary, #1d9e75); outline-offset: 2px; }',
    '.quick-look__scroll { overflow-y: auto; overscroll-behavior: contain; -webkit-overflow-scrolling: touch; flex: 1 1 auto; min-height: 0; }',

    /* Gallery (mobile first) */
    '.quick-look__gallery { background: var(--colorSmallImageBg, #f4f4f4); }',
    '.quick-look__stage { position: relative; }',
    '.quick-look__track { display: flex; overflow-x: auto; scroll-snap-type: x mandatory; scrollbar-width: none; overscroll-behavior-x: contain; }',
    '.quick-look__track::-webkit-scrollbar { display: none; }',
    '.quick-look__slide { flex: 0 0 100%; margin: 0; scroll-snap-align: start; height: 40vh; height: 40dvh; }',
    '.quick-look__slide img { width: 100%; height: 100%; object-fit: contain; display: block; }',
    '.quick-look__counter { position: absolute; top: 10px; left: 10px; margin: 0; font-size: 12px; padding: 2px 8px; border-radius: 20px; background: rgba(0, 0, 0, 0.55); color: #fff; }',
    '.quick-look__dots { display: flex; justify-content: center; gap: 4px; padding: 6px 0 8px; }',
    '.quick-look__dot { width: 24px; height: 24px; padding: 0; border: 0; background: none; cursor: pointer; display: flex; align-items: center; justify-content: center; }',
    '.quick-look__dot::before { content: ""; width: 8px; height: 8px; border-radius: 50%; background: var(--colorTextBody, #333); opacity: 0.25; transition: opacity 0.15s; }',
    '.quick-look__dot[aria-current="true"]::before { opacity: 0.85; }',
    '.quick-look__nav { display: none; }',

    /* Info */
    '.quick-look__info { padding: 1.1rem 1.5rem 1.5rem; }',
    '.quick-look__title { margin: 0 0 0.35rem; padding-right: 2.25rem; font-size: calc(var(--typeHeaderSize, 32px) * 0.6); line-height: 1.25; color: var(--colorTextBody, #1a1a1a); }',
    '.quick-look__price { margin: 0 0 0.6rem; color: var(--colorPrice, inherit); font-size: calc(var(--typeBaseSize, 16px) * 1.05); }',
    '.quick-look__compare { margin-left: 6px; opacity: 0.6; }',
    '.quick-look__variant { margin: -0.3rem 0 0.7rem; font-size: calc(var(--typeBaseSize, 16px) * 0.92); }',
    '.quick-look__variant[hidden] { display: none; }',
    '.quick-look__packaging { margin: 0 0 0.9rem; font-size: calc(var(--typeBaseSize, 16px) * 0.88); }',
    '.quick-look__packaging p { display: inline; margin: 0; }',
    '.quick-look__packaging-label { font-weight: 600; margin-right: 4px; }',
    '.quick-look__action { display: block; width: 100%; margin: 0 0 1rem; }',
    '.quick-look__action[disabled] { opacity: 0.7; cursor: default; }',
    '.quick-look__action-error { margin: -0.5rem 0 1rem; font-size: calc(var(--typeBaseSize, 16px) * 0.85); color: var(--colorSaleTag, #cc0000); }',
    '.quick-look__action-error[hidden] { display: none; }',
    '.quick-look__description { font-size: calc(var(--typeBaseSize, 16px) * 0.92); line-height: 1.65; max-height: 6.6em; overflow: hidden; }',
    '.quick-look__description.has-overflow:not(.is-expanded) { -webkit-mask-image: linear-gradient(to bottom, #000 55%, transparent); mask-image: linear-gradient(to bottom, #000 55%, transparent); }',
    '.quick-look__description.is-expanded { max-height: none; overflow: visible; }',
    '.quick-look__description > :first-child { margin-top: 0; }',
    '.quick-look__more { margin: 0.35rem 0 0; padding: 4px 0; border: 0; background: none; color: var(--colorLink, var(--colorBtnPrimary)); text-decoration: underline; cursor: pointer; font: inherit; }',
    '.quick-look__more[hidden] { display: none; }',
    '.quick-look__link { display: inline-block; margin-top: 1rem; color: var(--colorLink, var(--colorBtnPrimary)); text-decoration: underline; }',
    '.quick-look__status { padding: 3rem 1.5rem; text-align: center; }',
    '.quick-look__loading-dots { display: inline-flex; gap: 5px; }',
    '.quick-look__loading-dots span { width: 7px; height: 7px; border-radius: 50%; background: var(--colorBtnPrimary, #1d9e75); animation: mmsQlPulse 1.2s infinite; }',
    '.quick-look__loading-dots span:nth-child(2) { animation-delay: 0.2s; } .quick-look__loading-dots span:nth-child(3) { animation-delay: 0.4s; }',
    '@keyframes mmsQlPulse { 0%, 80%, 100% { opacity: 0.2; } 40% { opacity: 1; } }',
    '.quick-look__error { margin: 0 0 0.75rem; }',

    /* Desktop: two columns, gallery ~45% on the left */
    '@media (min-width: 750px) {',
    '  .quick-look-content { display: grid; grid-template-columns: 45% 1fr; align-items: start; }',
    '  .quick-look__gallery { padding: 20px 20px 4px; align-self: stretch; }',
    '  .quick-look__slide { height: auto; aspect-ratio: 1 / 1; }',
      '  .quick-look__nav {',
    '    display: flex; position: absolute; top: 50%; transform: translateY(-50%);',
    '    width: 34px; height: 34px; border-radius: 50%; border: 0; cursor: pointer; align-items: center; justify-content: center;',
    '    background: var(--colorBody, #fff); color: var(--colorTextBody, #333); box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);',
    '  }',
    '  .quick-look__nav[disabled] { opacity: 0.35; cursor: default; }',
    '  .quick-look__nav--prev { left: 6px; } .quick-look__nav--next { right: 6px; }',
    '  .quick-look__info { padding: 28px 28px 24px 8px; }',
    '}',

    /* Phone: bottom sheet */
    '@media (max-width: 749px) {',
    '  dialog.quick-look {',
    '    width: 100vw; max-width: 100vw; height: auto; max-height: 92vh; max-height: 92dvh;',
    '    inset: auto 0 0 0; margin: 0; border-radius: 16px 16px 0 0;',
    '  }',
    '}'
  ].join('\n');

  function injectStyles() {
    if (document.getElementById('mms-quick-look-styles')) return;
    var style = document.createElement('style');
    style.id = 'mms-quick-look-styles';
    style.textContent = STYLES;
    document.head.appendChild(style);
  }

  // Opens the inserted content on one variant: its option label under the price, its
  // price, and the index of its image among the slides (0 when it has none). A variant
  // image beyond the first five arrives as a hidden [data-ql-extra] slide; it is moved to
  // the front and the last regular slide dropped, so the slide count (dots, counter) holds.
  function applyVariant(root, variantId) {
    var extras = root.querySelectorAll('[data-ql-extra]');
    var start = 0;
    var map = null;
    var node = variantId ? root.querySelector('[data-ql-variants]') : null;
    if (node) {
      try { map = JSON.parse(node.textContent); } catch (e) { map = null; }
    }
    var v = map && map[String(variantId)];
    if (v) {
      var line = root.querySelector('[data-ql-variant]');
      if (line && v.label) {
        line.textContent = v.label;
        line.hidden = false;
      }
      var price = root.querySelector('[data-ql-price]');
      if (price && v.price) {
        price.textContent = '';
        var current = document.createElement('span');
        current.textContent = v.price;
        price.appendChild(current);
        if (v.compare) {
          var compare = document.createElement('s');
          compare.className = 'quick-look__compare';
          compare.textContent = v.compare;
          price.appendChild(compare);
        }
      }
      if (v.image) {
        var slides = Array.prototype.slice.call(root.querySelectorAll('[data-ql-slide]'));
        var found = -1;
        slides.forEach(function (slide, i) {
          if (found === -1 && slide.getAttribute('data-image-id') === v.image) found = i;
        });
        if (found !== -1) {
          start = found;
        } else {
          var extra = root.querySelector('[data-ql-extra][data-image-id="' + v.image + '"]');
          var track = root.querySelector('[data-ql-track]');
          if (extra && track && slides.length) {
            extra.removeAttribute('data-ql-extra');
            extra.setAttribute('data-ql-slide', '');
            extra.hidden = false;
            var img = extra.querySelector('img');
            if (img) img.loading = 'eager';
            track.insertBefore(extra, slides[0]);
            if (slides.length >= 5) slides[slides.length - 1].remove();
            Array.prototype.forEach.call(root.querySelectorAll('[data-ql-slide]'), function (slide, i, all) {
              slide.setAttribute('aria-label', (i + 1) + ' of ' + all.length);
            });
            start = 0;
          }
        }
      }
    }
    Array.prototype.forEach.call(root.querySelectorAll('[data-ql-extra]'), function (el) { el.remove(); });
    return start;
  }

  // Wires prev/next, dots, arrow keys and the live counter for one inserted gallery,
  // starting on slide `start` (positioned instantly, without a smooth scroll).
  function initGallery(root, start) {
    var gallery = root.querySelector('[data-ql-gallery]');
    if (!gallery) return;
    var track = gallery.querySelector('[data-ql-track]');
    var slides = Array.prototype.slice.call(track.querySelectorAll('[data-ql-slide]'));
    var count = slides.length;
    if (count < 2) return;
    var prev = gallery.querySelector('[data-ql-prev]');
    var next = gallery.querySelector('[data-ql-next]');
    var counter = gallery.querySelector('[data-ql-counter]');
    var dots = Array.prototype.slice.call(gallery.querySelectorAll('[data-ql-dot]'));
    var current = 0;

    function indexFromScroll() {
      var width = track.clientWidth || 1;
      return Math.max(0, Math.min(count - 1, Math.round(track.scrollLeft / width)));
    }

    function render(index) {
      current = index;
      counter.textContent = (index + 1) + ' / ' + count;
      dots.forEach(function (dot, i) {
        if (i === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      if (prev) prev.disabled = index === 0;
      if (next) next.disabled = index === count - 1;
    }

    // While a button/dot/key scroll animates, ignore the intermediate positions so the
    // live counter only announces the destination.
    var pending = null;
    var pendingTimer = null;

    function go(index) {
      index = Math.max(0, Math.min(count - 1, index));
      pending = index;
      clearTimeout(pendingTimer);
      pendingTimer = setTimeout(function () { pending = null; }, 1000);
      track.scrollTo({ left: index * track.clientWidth, behavior: 'smooth' });
      render(index);
    }

    track.addEventListener('scroll', function () {
      var index = indexFromScroll();
      if (pending !== null) {
        if (index !== pending) return;
        pending = null;
        clearTimeout(pendingTimer);
      }
      if (index !== current) render(index);
    }, { passive: true });
    if (prev) prev.addEventListener('click', function () { go(current - 1); });
    if (next) next.addEventListener('click', function () { go(current + 1); });
    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () { go(i); });
    });
    track.addEventListener('keydown', function (evt) {
      if (evt.key === 'ArrowLeft') { evt.preventDefault(); go(current - 1); }
      else if (evt.key === 'ArrowRight') { evt.preventDefault(); go(current + 1); }
    });
    start = Math.max(0, Math.min(count - 1, start || 0));
    if (start) {
      // The start slide is lazy-loaded otherwise; fetch it now so it doesn't show blank.
      var startImg = slides[start].querySelector('img');
      if (startImg) startImg.loading = 'eager';
    }
    track.scrollLeft = start * track.clientWidth;
    render(start);
  }

  // Shows the More/Less toggle only when the clamped description actually overflows.
  function initDescription(root) {
    var desc = root.querySelector('[data-ql-desc]');
    var more = root.querySelector('[data-ql-more]');
    if (!desc || !more) return;
    // Clamped by max-height (4 lines); -webkit-line-clamp is not used because it hides
    // overflow without it showing up in scrollHeight when the text spans several blocks.
    var overflows = desc.scrollHeight > desc.clientHeight + 1;
    desc.classList.toggle('has-overflow', overflows);
    more.hidden = !overflows;
    more.addEventListener('click', function () {
      var expanded = desc.classList.toggle('is-expanded');
      more.textContent = expanded ? 'Less' : 'More';
      more.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    });
  }

  customElements.define(
    'mms-quick-look',
    class MMSQuickLookElement extends HTMLElement {
      connectedCallback() {
        if (this.dialog) return;
        injectStyles();
        this.innerHTML =
          '<dialog class="quick-look" aria-labelledby="MMSQuickLookTitle">' +
            '<button type="button" class="quick-look__close" data-quick-look-close aria-label="Close">&times;</button>' +
            '<div class="quick-look__scroll" data-quick-look-body></div>' +
          '</dialog>';
        this.dialog = this.querySelector('dialog');
        this.body = this.querySelector('[data-quick-look-body]');
        var self = this;

        this.querySelector('[data-quick-look-close]').addEventListener('click', function () {
          self.close();
        });

        // Backdrop: a click whose target is the dialog element itself lands outside the panel content.
        this.dialog.addEventListener('click', function (evt) {
          if (evt.target === self.dialog) self.close();
        });

        // Escape: the dialog closes on keydown via 'cancel'. Swallow the following keyup so
        // the drawer's window keyup handler doesn't close the drawer too, and close through
        // close() so cleanup runs synchronously.
        this.dialog.addEventListener('cancel', function (evt) {
          self._guardEscapeKeyup();
          evt.preventDefault();
          self.close();
        });

        // Fallback for any other way the dialog closes. Cleanup is not tied to this event:
        // browsers may deliver it late (seen when the page isn't being painted).
        this.dialog.addEventListener('close', function () {
          if (self._isOpen) {
            self._isOpen = false;
            self._afterClose();
          }
        });
      }

      open(handle, opener, action, variantId) {
        if (!handle) return;
        var self = this;
        this._handle = handle;
        this._variantId = variantId || null;
        this._opener = opener || document.activeElement;
        this._action = action && typeof action.run === 'function' ? action : null;

        if (!this.dialog.open) {
          this._place();
          this.dialog.showModal();
          this._isOpen = true;
          this._lockScroll();
        }

        this.body.innerHTML =
          '<div class="quick-look__status" role="status">' +
            '<span class="quick-look__loading-dots" aria-hidden="true"><span></span><span></span><span></span></span>' +
            '<span class="visually-hidden">Loading</span>' +
          '</div>';
        this.dialog.setAttribute('aria-busy', 'true');
        this.dialog.setAttribute('aria-label', 'Product details');
        this.dialog.removeAttribute('aria-labelledby');

        load(handle).then(
          function (html) {
            if (self._handle !== handle || !self.dialog.open) return;
            // Apply the variant before insertion: moving a slide inside a live
            // scroll-snap track makes the browser re-snap to the previously first slide.
            var tpl = document.createElement('template');
            tpl.innerHTML = html;
            var start = applyVariant(tpl.content, self._variantId);
            self.body.innerHTML = '';
            self.body.appendChild(tpl.content);
            self.body.scrollTop = 0;
            self._renderAction();
            initGallery(self.body, start);
            initDescription(self.body);
            self.dialog.removeAttribute('aria-label');
            self.dialog.setAttribute('aria-labelledby', 'MMSQuickLookTitle');
            self.dialog.removeAttribute('aria-busy');
          },
          function () {
            if (self._handle !== handle || !self.dialog.open) return;
            self.body.innerHTML =
              '<div class="quick-look__status">' +
                '<p class="quick-look__error" id="MMSQuickLookTitle">Sorry, we couldn’t load this product.</p>' +
                '<a class="quick-look__link" href="' + productUrl(handle) + '">Go to the product page</a>' +
              '</div>';
            self.dialog.removeAttribute('aria-label');
            self.dialog.setAttribute('aria-labelledby', 'MMSQuickLookTitle');
            self.dialog.removeAttribute('aria-busy');
          }
        );
      }

      close() {
        if (!this.dialog) return;
        var wasOpen = this._isOpen;
        this._isOpen = false;
        if (this.dialog.open) this.dialog.close();
        if (wasOpen) this._afterClose();
      }

      // ---------- internals ----------

      _renderAction() {
        var slot = this.body.querySelector('[data-ql-action-slot]');
        if (!slot) return;
        slot.innerHTML = '';
        var action = this._action;
        if (!action) return;
        var active = false;
        try { active = !!(action.isActive && action.isActive()); } catch (e) { active = false; }
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'btn btn--full quick-look__action';
        button.setAttribute('data-ql-action', active ? 'active' : 'inactive');
        button.textContent = active && action.activeLabel ? action.activeLabel : action.label;
        var error = document.createElement('p');
        error.className = 'quick-look__action-error';
        error.setAttribute('role', 'alert');
        error.hidden = true;
        var self = this;
        button.addEventListener('click', function () {
          if (button.disabled) return;
          var result;
          try { result = action.run(); } catch (e) { result = null; /* the caller's action must not break the popup */ }
          if (!result || typeof result.then !== 'function') {
            self.close();
            return;
          }
          // Async action: stay open while pending; close on success, show the error on failure.
          var idleLabel = button.textContent;
          button.disabled = true;
          button.setAttribute('aria-busy', 'true');
          button.textContent = action.pendingLabel || 'Adding…';
          error.hidden = true;
          error.textContent = '';
          result.then(
            function () {
              if (self._action === action) self.close();
            },
            function (err) {
              if (self._action !== action || !button.isConnected) return;
              button.disabled = false;
              button.removeAttribute('aria-busy');
              button.textContent = idleLabel;
              error.textContent = (err && err.message) || (typeof err === 'string' && err) || 'Sorry, something went wrong. Please try again.';
              error.hidden = false;
            }
          );
        });
        slot.appendChild(button);
        slot.appendChild(error);
      }

      // Focus target inside the cart drawer when the opener is gone (the upsell bar and
      // the line items are rebuilt with innerHTML).
      _drawerFallback() {
        var drawer = document.getElementById('CartDrawer');
        if (!drawer) return null;
        return drawer.querySelector('.js-drawer-close') || drawer.querySelector('.drawer__close button');
      }

      // After focus returns to an opener inside the drawer, a later redraw can still remove
      // it (e.g. the bar rebuilding on cart:updated). Move focus to the fallback if that
      // happens while it is focused.
      _watchOpener(opener) {
        var drawer = document.getElementById('CartDrawer');
        if (!drawer || typeof MutationObserver === 'undefined') return;
        var self = this;
        var observer = new MutationObserver(function () {
          if (opener.isConnected) return;
          stop();
          var active = document.activeElement;
          if (!active || active === document.body || !active.isConnected) {
            var fallback = self._drawerFallback();
            if (fallback) fallback.focus();
          }
        });
        var timer = setTimeout(stop, 3000);
        function stop() {
          observer.disconnect();
          clearTimeout(timer);
        }
        observer.observe(drawer, { childList: true, subtree: true });
      }

      _place() {
        var drawer = document.getElementById('CartDrawer');
        var inDrawer = !!(drawer && this._opener && drawer.contains(this._opener));
        this._fromDrawer = inDrawer;
        var parent = inDrawer ? drawer : document.body;
        if (this.parentNode !== parent) parent.appendChild(this);
      }

      _lockScroll() {
        if (this._fromDrawer) return;
        var html = document.documentElement;
        var body = document.body;
        this._savedScroll = {
          htmlOverflow: html.style.overflow,
          bodyOverflow: body.style.overflow,
          bodyPaddingRight: body.style.paddingRight
        };
        var scrollbar = window.innerWidth - html.clientWidth;
        html.style.overflow = 'hidden';
        body.style.overflow = 'hidden';
        if (scrollbar > 0) body.style.paddingRight = scrollbar + 'px';
      }

      _unlockScroll() {
        if (!this._savedScroll) return;
        var html = document.documentElement;
        var body = document.body;
        html.style.overflow = this._savedScroll.htmlOverflow;
        body.style.overflow = this._savedScroll.bodyOverflow;
        body.style.paddingRight = this._savedScroll.bodyPaddingRight;
        this._savedScroll = null;
      }

      _guardEscapeKeyup() {
        var timer = null;
        var guard = function (evt) {
          if (evt.key !== 'Escape' && evt.keyCode !== 27) return;
          evt.stopPropagation();
          evt.stopImmediatePropagation();
          remove();
        };
        var remove = function () {
          window.removeEventListener('keyup', guard, true);
          clearTimeout(timer);
        };
        window.addEventListener('keyup', guard, true);
        timer = setTimeout(remove, ESC_GUARD_MS);
      }

      _afterClose() {
        this._unlockScroll();
        this._handle = null;
        this._action = null;
        var opener = this._opener;
        this._opener = null;
        if (opener && opener.isConnected && typeof opener.focus === 'function') {
          opener.focus();
          if (this._fromDrawer) this._watchOpener(opener);
        } else if (this._fromDrawer) {
          var fallback = this._drawerFallback();
          if (fallback) fallback.focus();
        }
      }
    }
  );

  function instance() {
    var el = document.querySelector('mms-quick-look');
    if (!el) {
      el = document.createElement('mms-quick-look');
      document.body.appendChild(el);
    }
    return el;
  }

  function handleFor(el) {
    if (el.dataset.quickLookHandle) return el.dataset.quickLookHandle;
    var holder = el.closest('[data-quick-look-handle]');
    return holder ? holder.dataset.quickLookHandle : null;
  }

  window.MMSQuickLook = Object.freeze({
    open: function (handle, options) {
      instance().open(handle, options && options.opener, options && options.action, options && options.variantId);
    },
    close: function () {
      var el = document.querySelector('mms-quick-look');
      if (el) el.close();
    },
    prefetch: function (handle) {
      if (handle) load(handle).catch(function () {});
    }
  });

  // Declarative triggers (delegated, so markup rebuilt with innerHTML keeps working).
  document.addEventListener('click', function (evt) {
    var trigger = evt.target.closest && evt.target.closest('[data-quick-look-trigger]');
    if (!trigger) return;
    var handle = handleFor(trigger);
    if (!handle) return;
    evt.preventDefault();
    window.MMSQuickLook.open(handle, { opener: trigger });
  });

  function prefetchFrom(evt) {
    var trigger = evt.target.closest && evt.target.closest('[data-quick-look-trigger], [data-quick-look-prefetch]');
    if (trigger) window.MMSQuickLook.prefetch(handleFor(trigger));
  }
  document.addEventListener('pointerover', prefetchFrom, { passive: true });
  document.addEventListener('focusin', prefetchFrom);
})();
